import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Cinema } from '../../models';
import { CinemaService } from '../../services/cinema.service';
import { CompactCinemaRowComponent } from '../../components/compact-cinema-row.component';
import { LocationFilterService } from '../../services/location-filter.service';

@Component({
  selector: 'app-cinemas',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, CompactCinemaRowComponent],
  template: `
    <section class="listing-shell">
      <div class="container listing-top">
        <div class="listing-head">
          <div>
            <h1>Cinemas</h1>
            <p>{{ filteredCinemas().length }} venue{{ filteredCinemas().length === 1 ? '' : 's' }}</p>
          </div>
          <a class="mini-link" routerLink="/">Showtimes</a>
        </div>

        <div class="glass listing-search">
          <input class="input search-input" [(ngModel)]="query" placeholder="Search cinema, town, or chain" (ngModelChange)="scheduleQueryUpdate()" (keyup.enter)="updateQueryParams()" />
          <div class="filter-line">
            <select [(ngModel)]="county" (ngModelChange)="updateQueryParams()"><option value="">All counties</option><option *ngFor="let county of counties">{{ county }}</option></select>
            <select [(ngModel)]="chain" (ngModelChange)="updateQueryParams()"><option value="">All chains</option><option *ngFor="let chain of chains">{{ chain }}</option></select>
            <button class="clear-btn" type="button" (click)="clearFilters()">Clear</button>
          </div>
        </div>
      </div>
    </section>

    <section class="container listing-results">
      <div *ngIf="error" class="error-state">{{ error }}</div>
      <div *ngIf="loading" class="list-skeleton"><div *ngFor="let item of skeletons"></div></div>

      <div class="result-head" *ngIf="!loading">
        <h2>{{ query || county || chain ? 'Matching cinemas' : 'All cinemas' }}</h2>
        <span>{{ filteredCinemas().length }} found</span>
      </div>

      <div class="compact-list" *ngIf="!loading && filteredCinemas().length">
        <app-compact-cinema-row *ngFor="let cinema of filteredCinemas()" [cinema]="cinema" />
      </div>

      <div *ngIf="!loading && !filteredCinemas().length && !error" class="empty-state">
        No cinemas match those filters.
      </div>
    </section>
  `,
  styles: [
    `
      .listing-shell { position: sticky; top: 68px; z-index: 20; padding: 14px 0 8px; background: linear-gradient(180deg, var(--bg) 0%, rgba(7, 9, 20, .94) 72%, transparent 100%); backdrop-filter: blur(14px); }
      .listing-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 9px; }
      .listing-head h1 { margin: 0 0 2px; font-size: clamp(25px, 4vw, 38px); line-height: 1; }
      .listing-head p { margin: 0; color: var(--muted); font-size: 13px; font-weight: 850; }
      .mini-link { min-height: 36px; border: 1px solid var(--line); border-radius: 999px; padding: 8px 12px; color: var(--cyan); background: var(--control-bg); font-size: 13px; font-weight: 900; }
      .listing-search { display: grid; grid-template-columns: minmax(260px, 1fr) minmax(320px, .8fr); gap: 10px; padding: 10px; border-radius: 18px; box-shadow: 0 12px 34px rgba(0,0,0,.22); }
      .listing-search select { min-height: 46px; border: 1px solid var(--line); border-radius: 999px; padding: 0 14px; color: var(--text); background: var(--field-bg); }
      .search-input { font-weight: 850; }
      .filter-line { display: grid; grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) auto; gap: 8px; }
      .clear-btn { min-height: 46px; border: 1px solid var(--line); border-radius: 999px; padding: 0 14px; color: var(--text); background: var(--control-bg); font-weight: 900; cursor: pointer; }
      .listing-results { padding: 8px 0 64px; }
      .result-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 6px 0 9px; }
      .result-head h2 { margin: 0; font-size: 18px; line-height: 1.1; }
      .result-head span { color: var(--muted); font-size: 12px; font-weight: 900; }
      .compact-list { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .list-skeleton { display: grid; gap: 8px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .list-skeleton div { min-height: 68px; border-radius: 16px; background: var(--skeleton); background-size: 220% 100%; animation: shimmer 1.25s linear infinite; }
      @media (max-width: 1024px) {
        .listing-shell { top: 64px; }
        .listing-search { grid-template-columns: 1fr; }
      }
      @media (max-width: 700px) {
        .listing-shell { padding: 10px 0 8px; }
        .listing-head h1 { font-size: 24px; }
        .listing-head p { font-size: 12px; }
        .mini-link { display: none; }
        .listing-search { padding: 8px; gap: 8px; border-radius: 16px; }
        .filter-line { grid-template-columns: 1fr 1fr; }
        .compact-list, .list-skeleton { grid-template-columns: 1fr; }
      }
      @media (max-width: 380px) {
        .filter-line { grid-template-columns: 1fr; }
      }
    `,
  ],
})
export class CinemasComponent implements OnInit {
  query = '';
  county = '';
  chain = '';
  cinemas: Cinema[] = [];
  private rawCinemas: Cinema[] = [];
  loading = true;
  error = '';
  skeletons = Array.from({ length: 6 });
  private queryTimer?: ReturnType<typeof setTimeout>;
  private locationScopeKey = 'all';
  counties = ['Antrim', 'Armagh', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal', 'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Laois', 'Limerick', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone', 'Waterford', 'Westmeath', 'Wexford', 'Wicklow'];
  chains = ['Arc', 'Cineworld', 'Eclipse', 'IFI', 'IMC', 'Light House', 'Movies@', 'ODEON', 'Omniplex', 'Stella', 'Vue'];
  constructor(
    private readonly cinemaService: CinemaService,
    private readonly location: LocationFilterService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}
  ngOnInit() {
    this.location.state$.subscribe(() => {
      if (this.clearCinemasFiltersForNewLocationScope()) return;
      if (this.location.state.mode !== 'all') {
        this.load();
      } else {
        this.applyLocationFilter();
      }
    });
    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('query') ?? '';
      this.county = params.get('county') ?? '';
      this.chain = params.get('chain') ?? '';
      this.load();
    });
  }
  load() {
    this.loading = true;
    this.error = '';
    const county = this.location.state.mode !== 'all' ? '' : this.county;
    this.cinemaService.list({ county, chain: this.chain }).subscribe({
      next: (cinemas) => {
        this.rawCinemas = cinemas;
        this.applyLocationFilter();
        this.loading = false;
      },
      error: () => { this.error = 'Could not load cinemas. Make sure the backend is running.'; this.loading = false; },
    });
  }
  clearFilters() {
    this.query = '';
    this.county = '';
    this.chain = '';
    this.updateQueryParams();
  }
  scheduleQueryUpdate() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => this.updateQueryParams(), 350);
  }
  updateQueryParams() {
    if (this.queryTimer) clearTimeout(this.queryTimer);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: this.query.trim() || null,
        county: this.county || null,
        chain: this.chain || null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  filteredCinemas(): Cinema[] {
    const query = this.query.trim().toLowerCase();
    if (!query) return this.cinemas;
    return this.cinemas.filter((cinema) => [cinema.name, cinema.city, cinema.county, cinema.chain].join(' ').toLowerCase().includes(query));
  }

  private applyLocationFilter() {
    this.cinemas = this.location.sortCinemasByDistance(this.location.filterCinemas(this.rawCinemas));
  }

  private clearCinemasFiltersForNewLocationScope(): boolean {
    const state = this.location.state;
    const scopeKey = state.mode === 'manual' ? `manual:${state.label ?? ''}` : state.mode;
    const changed = scopeKey !== this.locationScopeKey;
    this.locationScopeKey = scopeKey;

    if (!changed || state.mode === 'all') return false;

    const hasPageFilters = Boolean(this.query || this.county || this.chain);
    if (!hasPageFilters) return false;

    this.query = '';
    this.county = '';
    this.chain = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        query: null,
        county: null,
        chain: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return true;
  }
}
